import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Lock, X } from 'lucide-react';
import UpgradeModal from './UpgradeModal';
import TopupModal from './TopupModal';
import { billingApiClient, isBillingConfigured, CreditPack } from '../../services/billingApi';
import { paywallBus } from '../../utils/paywallBus';
import { paywallCtaKind, paywallCtaLabel, type PaywallInfo } from '../../utils/paywall';

/**
 * Shows any 402 the current page did not explain itself — with the server's
 * own sentence and the action that fixes it (Renew / Subscribe / Buy
 * credits / Upgrade). Mounted once in the app shell. Cleared on navigation:
 * it belongs to the action that triggered it, not to the next page.
 */
const GlobalPaywallHost: React.FC = () => {
  const [info, setInfo] = useState<PaywallInfo | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const location = useLocation();

  useEffect(() => paywallBus.subscribe(setInfo), []);
  useEffect(() => {
    setInfo(null);
  }, [location.pathname]);

  const vertical = info?.vertical ?? sessionStorage.getItem('nyayabodh_active_role');
  const ctaKind = info ? paywallCtaKind(info.code) : 'upgrade';

  // credits_exhausted needs this vertical's top-up packs to open TopupModal —
  // fetched lazily, only once a credits paywall actually fires, same pattern
  // PlanSummaryCard already uses for the same call.
  useEffect(() => {
    if (!info || ctaKind !== 'topup' || !vertical || !isBillingConfigured()) return;
    let cancelled = false;
    billingApiClient.getCreditPacks(vertical).then((p) => {
      if (!cancelled) setPacks(p);
    }).catch(() => {
      /* the CTA button simply stays disabled-looking (empty packs) */
    });
    return () => { cancelled = true; };
  }, [info, ctaKind, vertical]);

  if (!info) return null;
  const canAct = isBillingConfigured() && !!vertical;

  return (
    <>
      <div
        role="alert"
        className="fixed inset-x-4 bottom-24 z-40 mx-auto flex max-w-xl items-start gap-3 border border-chamber-border bg-chamber-panel px-4 py-3.5 text-parchment-card shadow-elevation-4 lg:bottom-6"
      >
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-brass" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm leading-relaxed">{info.message}</p>
        {canAct && (
          <button
            type="button"
            onClick={() => (ctaKind === 'topup' ? setTopupOpen(true) : setUpgradeOpen(true))}
            className="shrink-0 bg-brass px-3 py-1.5 text-sm font-semibold text-chamber-ink hover:bg-brass-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brass focus-visible:ring-offset-2 focus-visible:ring-offset-chamber-panel"
          >
            {paywallCtaLabel(info.code, info.lapsed)}
          </button>
        )}
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setInfo(null)}
          className="shrink-0 p-1 text-chamber-muted hover:text-parchment-card focus:outline-none focus-visible:ring-2 focus-visible:ring-brass"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {vertical && (
        <UpgradeModal
          vertical={vertical}
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          onActivated={() => {
            setUpgradeOpen(false);
            setInfo(null);
          }}
        />
      )}
      <TopupModal
        open={topupOpen}
        packs={packs}
        onClose={() => setTopupOpen(false)}
        onGranted={() => {
          setTopupOpen(false);
          setInfo(null);
        }}
      />
    </>
  );
};

export default GlobalPaywallHost;
